/**
 * Money that should have arrived and was never recorded.
 *
 * The calendar says what is coming (shared/moneyCalendar.ts). This says what
 * came and did not: a deal payment whose date has passed and is still marked
 * unpaid, and a paycheck that landed in no record of income. An athlete who
 * is told "$24,583, week of Oct 6" and never sees it is the whole reason the
 * calendar exists, so the agency has to hear about it first.
 *
 * TWO KINDS, BECAUSE THEY ARE KNOWN DIFFERENTLY:
 *
 *   A deal payment is the agency's own record. They wrote "due 1 October",
 *   nobody has marked it paid, and the date has gone. That is a fact about
 *   their book, so it is flagged for every agency.
 *
 *   A paycheck is a projection. Reppr only knows it was missed if the agency
 *   records income at all — and most do not yet. Flagging a missed paycheck
 *   for an agency that records nothing would mean flagging every paycheck of
 *   every athlete, for ever, which teaches people to ignore the alerts. So a
 *   client is only watched for missed paychecks once income has been recorded
 *   for them: at least one receipt, recently. Otherwise the answer is "we
 *   cannot tell", which this returns as a silence rather than a flag.
 *
 * Nothing here decides whether money is late — it reports what the records
 * say, and the agency confirms or corrects it.
 */

import type { MoneyItem } from './moneyCalendar.js'

export interface Receipt {
  /** YYYY-MM-DD. */
  date: string
  amount: number
  contract_id?: string | null
  deal_id?: string | null
}

export type MissedKind = 'deal_payment_unpaid' | 'paycheck_unrecorded'

export interface MissedMoney {
  kind: MissedKind
  /** The contract or deal it came from. */
  source_id: string
  source_title: string | null
  /** The date it was expected, YYYY-MM-DD. */
  date: string
  amount: number | null
  days_late: number
  reason: string
}

const DAY = 24 * 60 * 60 * 1000

function dayOf(s: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(s ?? ''))
  if (!m) return null
  const t = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
  return Number.isNaN(t) ? null : t
}

export interface MissedMoneyInput {
  /** Everything the calendar dated for this client, past and future. */
  items: MoneyItem[]
  /** Income the agency recorded for this client. */
  receipts: Receipt[]
  today: string
  /**
   * Days after the date before anything is called missed. A club pays in the
   * week, not on the day, and an agency books receipts when it gets to them.
   */
  graceDays?: number
  /**
   * A receipt this many days either side of a check can answer for it — but
   * only for ONE check, and only the nearest. Paychecks come weekly, so a
   * window of seven days reaches the neighbours on both sides: before this
   * was one-to-one, a single recorded receipt silenced three weeks, and an
   * athlete whose last two checks were logged and whose third never arrived
   * was reported as fully paid.
   */
  windowDays?: number
  /** No receipt in this many days means the agency does not record income yet. */
  trackedWithinDays?: number
  /**
   * Whether a paycheck needs the client to have recorded income before it
   * counts (the default, true).
   *
   * That gate stops an agency that records nothing being told every check is
   * missing. It is about ALARMING people, not about recording: asked "what
   * could I record?", the answer includes the untracked client's checks,
   * because recording one is exactly how they stop being untracked.
   */
  requireTracked?: boolean
  /**
   * Earliest date each source can owe anything, by source id — a contract's
   * start date. Paychecks are projected from the season's first game, so a
   * contract signed in November would otherwise be "missing" every check
   * since September, none of which it ever owed.
   */
  notBefore?: Record<string, string>
}

export function findMissedMoney(input: MissedMoneyInput): MissedMoney[] {
  const today = dayOf(input.today)
  if (today == null) throw new Error(`findMissedMoney: today "${input.today}" is not a date`)
  const grace = (input.graceDays ?? 5) * DAY
  const window = (input.windowDays ?? 7) * DAY
  const trackedWithin = (input.trackedWithinDays ?? 90) * DAY

  const receipts = input.receipts
    .map(r => ({ ...r, at: dayOf(r.date) }))
    .filter((r): r is Receipt & { at: number } => r.at != null)

  // Does this agency record what arrives for this client? Without that, a
  // missing paycheck is indistinguishable from a paycheck nobody wrote down.
  const tracked = input.requireTracked === false || receipts.some(r => today - r.at <= trackedWithin)

  /**
   * Which checks a receipt answers for: one each, nearest first.
   *
   * Every paycheck in the window is a candidate, including ones still inside
   * the grace period, so a receipt for last Monday's check is not consumed by
   * the check a week before it. Receipts are taken in date order and each
   * claims the nearest unclaimed check of the same contract; a receipt
   * recorded against no contract can answer any of them.
   */
  const paychecks = input.items
    .map((item, index) => ({ item, index, at: dayOf(item.date) }))
    .filter((c): c is { item: MoneyItem; index: number; at: number } =>
      c.item.kind === 'paycheck' && c.item.status !== 'received' && c.at != null)
  const answered = new Set<number>()
  for (const r of [...receipts].sort((a, b) => a.at - b.at)) {
    let best: { index: number; distance: number } | null = null
    for (const c of paychecks) {
      if (answered.has(c.index)) continue
      if (r.contract_id && r.contract_id !== c.item.source_id) continue
      const distance = Math.abs(r.at - c.at)
      if (distance > window) continue
      if (!best || distance < best.distance) best = { index: c.index, distance }
    }
    if (best) answered.add(best.index)
  }

  const missed: MissedMoney[] = []
  for (const [index, item] of input.items.entries()) {
    const at = dayOf(item.date)
    if (at == null || item.status === 'received') continue
    if (today - at < grace) continue
    const starts = input.notBefore?.[item.source_id]
    if (starts) {
      const from = dayOf(starts)
      if (from != null && at < from) continue
    }
    const daysLate = Math.floor((today - at) / DAY)

    if (item.kind === 'deal_payment') {
      missed.push({
        kind: 'deal_payment_unpaid',
        source_id: item.source_id,
        source_title: item.source_title,
        date: item.date,
        amount: item.amount,
        days_late: daysLate,
        reason: `Due ${daysLate} days ago and still not marked paid.`,
      })
      continue
    }

    if (item.kind !== 'paycheck' || !tracked) continue
    if (answered.has(index)) continue
    missed.push({
      kind: 'paycheck_unrecorded',
      source_id: item.source_id,
      source_title: item.source_title,
      date: item.date,
      amount: item.amount,
      days_late: daysLate,
      reason: `Expected the week of ${item.date}; no income recorded for it.`,
    })
  }

  return missed.sort((a, b) => a.date.localeCompare(b.date))
}

/**
 * Whether a client can be watched for missed paychecks at all, and why not.
 * The agency's screen says this rather than showing an empty list that looks
 * like good news.
 */
export function paycheckWatchState(receipts: Receipt[], today: string, trackedWithinDays = 90): { watched: boolean; reason: string | null } {
  const now = dayOf(today)
  if (now == null) throw new Error(`paycheckWatchState: today "${today}" is not a date`)
  const recent = receipts.some(r => {
    const at = dayOf(r.date)
    return at != null && now - at <= trackedWithinDays * DAY
  })
  return recent
    ? { watched: true, reason: null }
    : { watched: false, reason: 'No income has been recorded for this athlete, so Reppr cannot tell a missed paycheck from one nobody wrote down. Log a payment and it starts watching.' }
}

export interface AssumablePaycheck {
  /** The contract. */
  source_id: string
  source_title: string | null
  /** YYYY-MM-DD, the week the check landed. */
  date: string
  amount: number
  detail: string
}

/**
 * The paychecks to record as paid without anyone saying so.
 *
 * Jack, 30 Sep 2026: "the checks should be assumed if they were active that
 * week". Asking an agent to tick every check of every athlete, every week,
 * was the friction; a club pays a player on its roster. So a check is taken
 * as paid when all of these hold:
 *
 *   - its date has passed, and nothing recorded answers it (the same
 *     one-receipt-one-check matching as findMissedMoney);
 *   - the contract's roster history covers that week (standing_known), so the
 *     amount is what that week paid — active, the split on IR, the squad rate
 *     — and not today's status projected backwards;
 *   - the amount is known (an elevated practice-squad week is not);
 *   - nobody has said it did not arrive. A week marked "didn't arrive" is
 *     never assumed again: it stays missing, which is what the missed-payment
 *     alert is for.
 *
 * A week the history does not cover is left for a person, on "Has this
 * arrived?", exactly as before.
 */
export function assumablePaychecks(input: {
  items: MoneyItem[]
  receipts: Receipt[]
  today: string
  notBefore?: Record<string, string>
  /** Dates marked "didn't arrive", by contract id. */
  notReceived?: Record<string, string[]>
}): AssumablePaycheck[] {
  const unanswered = findMissedMoney({
    items: input.items,
    receipts: input.receipts,
    today: input.today,
    notBefore: input.notBefore,
    requireTracked: false,
    graceDays: 0,
  }).filter(m => m.kind === 'paycheck_unrecorded')
  const checks = new Map(input.items.filter(i => i.kind === 'paycheck').map(i => [`${i.source_id}:${i.date}`, i]))
  const out: AssumablePaycheck[] = []
  for (const m of unanswered) {
    const item = checks.get(`${m.source_id}:${m.date}`)
    if (!item || item.standing_known !== true || item.amount == null) continue
    if (input.notReceived?.[m.source_id]?.includes(m.date)) continue
    out.push({ source_id: m.source_id, source_title: m.source_title, date: m.date, amount: Math.round(item.amount * 100) / 100, detail: item.detail })
  }
  return out
}
